import { useRef, useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'motion/react';
import { ClientPortal } from '@components';

// Utils
import { easeOutQuad } from '@/core/easing';
import { ensurePostsAreRHTVVideos, getHighlightMarkup } from '@/core/utils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
import { isTouchDevice } from '@/utils/touchDetection';
import debounce from "lodash.debounce";

// RHTV components
import RHTV_Logo from './Logo/RHTV-Logo';
import RHTV_Channels from './Channels/RHTV-Channels';
import RHTV_Modal from './Modal/RHTV-Modal';
import { useRockHallTV } from '@/providers/RockHallTVContext';

// GSAP
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useGSAP } from "@gsap/react";
import Observer from "gsap/Observer";
import { Draggable } from "gsap/Draggable";

// Lenis
import {
  startLenis,
  stopLenis,
  pauseLenis,
  unpauseLenis,
} from './utils/lenis-helper';

// Styles
import styles from './RockHallTv.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

// Debug utils
import { getBooleanParam } from '@/utils/urlParams';
import { useDebug } from '@/utils/useDebugLog';
const { log } = useDebug({ name: 'RockHallTv', bgColor: '#13678A', textColor: 'white', enabled: false });
const { log: logGreen } = useDebug({ name: 'RockHallTv', bgColor: 'lime', enabled: false });

// Constants
import {
  // BORDER_RADIUS, // Not using atm -Gabe
  ANIMATION_CONFIG,
  LENIS_CONFIG,
  SCROLLTRIGGER_CONFIG,
  RESIZE_DEBOUNCE_MS,
  CHANNEL_LAYOUT
} from './constants';

// ----------------------------------------

interface RockHallTvProps {
  channels: RockHallTvChannel[];
  content: RockHallTvContent;
}

export default function RHTV({ channels, content }: RockHallTvProps) {

  const {
    showModal,
    activeChannel,
    activeVideo,
    setShowModal,
    setActiveChannel,
    setActiveVideo,
    updateRHTVRoute
  } = useRockHallTV();

  const router = useRouter();

  // Wait for ViewSizeContext to finish its first measurement before initializing
  // This prevents using SSR default values (1440x900 desktop) which could be wrong
  const { isDesktop, isReady: isViewSizeReady } = useViewSize();
  const { mounted } = useClientMount();

  // -------------------- Content
  const { labels } = usePageWrapperData();
  const { headline, subhead, videoTrayOpen, videoTrayClose, rrtvAriaLabels } = content;
  const { scrollPrompt } = labels || {};
  const headlineMarkup = getHighlightMarkup(headline);

  // -------------------- DEV TOGGLES

  const isDebugMode = getBooleanParam('debug');
  const DEV_DISABLE_LENIS = false;
  const DEV_FORCE_TOUCH_DEVICE = false;

  // -------------------- TOUCH DEVICE DETECTION:
  // - Touch devices use native horizontal scrolling for the video tray
  // - Lenis smooth scroll is completely disabled on touch devices
  // - ScrollTrigger is still used for kick/slide animations (but not for video tray)
  const [isTouchDeviceState, setIsTouchDeviceState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return DEV_FORCE_TOUCH_DEVICE || isTouchDevice();
  });

  // Helper to check if viewport is in portrait orientation
  const getIsPortrait = () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

  // -------------------- Intro Screen (Pre-kick state)
  // Desktop non-touch users see the intro screen and must scroll/interact to "kick" into the main experience
  // Touch device users (including tablets/iPads) bypass the intro and start directly in the kicked state (no reverse kick either)
  const [showIntro, setShowIntro] = useState<boolean | null>(null); // null until determined
  const [isReloading, setIsReloading] = useState(false);
  const hasTriggeredReloadRef = useRef(false);
  const previousShowIntroRef = useRef<boolean | null>(null); // Track previous showIntro value to detect changes
  const isAbortingReloadRef = useRef(false); // Flag to bypass normal logic during abort/reload

  // Loading overlay - starts visible, only fades out when we KNOW the screen size and everything is ready
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [isClientReady, setIsClientReady] = useState(false); // Track if we know screen size
  const [isAnimationsReady, setIsAnimationsReady] = useState(false); // Track if GSAP is initialized

  // Orientation mode: Portrait vs Landscape
  const [isPortraitMode, setIsPortraitMode] = useState(false); // Initialize as false for SSR, update in useEffect

  // -------------------- Elements
  //#region Elements

  let HTML: HTMLHtmlElement | null = null;
  let BODY: HTMLBodyElement | null = null;

  // Initialize client-only values on mount (HTML/BODY refs and showIntro state)
  useEffect(() => {
    // Wait until both ViewSizeContext has finished its first measurement AND client has mounted
    if (!isViewSizeReady || !mounted) return;

    // Initialize HTML and BODY references
    if (typeof document !== 'undefined') {
      HTML = document.documentElement as HTMLHtmlElement;
      BODY = document.body as HTMLBodyElement;
    }

    // Initialize showIntro based on device type and orientation
    // Touch devices ALWAYS skip intro (regardless of screen width)
    // Non-touch devices show intro only if: desktop AND landscape orientation
    const currentIsPortrait = getIsPortrait();
    const shouldShowIntro = !isTouchDeviceState && isDesktop && !currentIsPortrait;
    setShowIntro(shouldShowIntro);

    // If NOT showing intro (mobile/tablet), mark kick as already complete
    if (!shouldShowIntro) {
      setIsKickStarted(true);
      setIsKickComplete(true);
      isKickStartedRef.current = true;
      isKickCompleteRef.current = true;
    }

    // Mark that we now know what screen size we're on
    setIsClientReady(true);

    log('Client ready - screen size determined', { isDesktop, isTouchDeviceState, currentIsPortrait, shouldShowIntro });
  }, [isViewSizeReady, mounted, isDesktop, isTouchDeviceState]);

  // Fade out loading overlay only when BOTH client is ready AND animations are initialized
  useEffect(() => {
    if (isClientReady && isAnimationsReady) {
      log('Everything ready - fading out loading overlay');

      // Small delay to ensure everything is visually settled
      const fadeTimer = setTimeout(() => {
        setShowLoadingOverlay(false);
      }, 300);

      return () => clearTimeout(fadeTimer);
    }
  }, [isClientReady, isAnimationsReady]);

  // Update showIntro when orientation changes (browser resize/rotation)
  // Reload the page when showIntro state changes to ensure clean state
  useEffect(() => {
    // Skip if we haven't determined screen size yet (initial load)
    if (!isClientReady) return;

    // Only trigger reload once
    if (hasTriggeredReloadRef.current) return;

    // Get the previous value of showIntro
    const wasShowingIntro = previousShowIntroRef.current;

    // Update the ref with current value for next time
    previousShowIntroRef.current = showIntro;

    // If showIntro state changed AFTER initial load, reload the page for a clean slate
    if (wasShowingIntro !== null && wasShowingIntro !== showIntro) {
      log(`ShowIntro changed: ${wasShowingIntro ? 'intro → no-intro' : 'no-intro → intro'} - ABORTING AND RELOADING`);
      hasTriggeredReloadRef.current = true;
      isAbortingReloadRef.current = true; // Set abort flag to bypass normal logic

      // ABORT: Close player if open
      if (showModal) {
        log('ABORT: Closing player');
        setShowModal(false);
        setIsModalExiting(false);
      }

      // ABORT: Clear URL deeplink
      log('ABORT: Clearing URL deeplink');
      updateRHTVRoute();

      // ABORT: Show loading overlay to cover broken UI state
      log('ABORT: Showing loading overlay');
      setIsReloading(true);
      setShowLoadingOverlay(true);

      // ABORT: Reload after allowing React to flush state updates to DOM
      log('ABORT: Triggering page reload');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.location.reload();
        });
      });

      // No cleanup - returning early prevents registering cleanup that would cancel reload
      return;
    }
  }, [isPortraitMode, showIntro, isTouchDeviceState, isClientReady, updateRHTVRoute]);

  const rootEl = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const headerTextRef = useRef<HTMLDivElement | null>(null);
  const logoRef = useRef<HTMLDivElement | null>(null);
  const scrollCtaRef = useRef<HTMLDivElement | null>(null);
  const scrollCtaCircleRef = useRef<SVGCircleElement | null>(null);
  const channelsContainerRef = useRef<HTMLDivElement>(null);
  const channelsViewportRef = useRef<HTMLDivElement>(null);
  const channelsRowRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Memoize height calculations to avoid unnecessary recalculations
  const heightSection = useCallback(() => rootEl.current?.offsetHeight || 0, []);
  const heightHeader = useCallback(() => headerRef.current?.offsetHeight || 0, []);
  const heightChannelRow = useCallback(() => heightSection() - heightHeader(), [heightSection, heightHeader]);

  // Helper functions for body scroll locking
  const bodyScrollLock = useCallback(() => {
    BODY?.classList.add("locked");
  }, []);

  const bodyScrollUnlock = useCallback(() => {
    BODY?.classList.remove("locked");
  }, []);

  const lenisCleanupClasses = useCallback(() => {
    HTML?.classList.remove("lenis", "lenis-smooth", "lenis-stopped", "lenis-scrolling");
  }, []);

  //#endregion Elements
  // -------------------- Animations
  //#region Animations

  const tlKickRef = useRef<gsap.core.Timeline | null>(null);        // Timeline
  const tlReverseRef = useRef<gsap.core.Timeline | null>(null);     // Timeline
  const tlScaleUpRef = useRef<gsap.core.Timeline | null>(null);     // Timeline
  const scrollCtaAnimRef = useRef<gsap.core.Timeline | null>(null); // Timeline
  const tlSlideRef = useRef<gsap.core.Timeline | null>(null);       // Timeline
  const stSlideRef = useRef<ScrollTrigger | null>(null);            // ScrollTrigger
  const draggableRef = useRef<Draggable | null>(null);              // Draggable
  const isSyncingFromDraggableRef = useRef(false);                  // Flag to prevent circular updates
  const reverseGestureObserverRef = useRef<Observer | null>(null);  // Observer for reverse kick gesture

  //#endregion Animations
  // -------------------- States + Refs

  // Kick animation states (used by classes/UI)
  // Initialize based on showIntro - but showIntro starts as null, so these start as false
  // They'll be set correctly once showIntro is determined
  const [isKickStarted, setIsKickStarted] = useState(false);
  const [isKickComplete, setIsKickComplete] = useState(false);

  // Logic refs (no re-render) - will be set when showIntro is determined
  const isKickStartedRef = useRef(false);
  const isKickCompleteRef = useRef(false);
  const isReverseStartedRef = useRef(false);

  // Ref to store the kick trigger function
  const triggerKickCallbackRef = useRef<(() => void) | null>(null);

  // Ref to store the destroy reverse gesture observer function
  const reverseGestureObserverDestroyRef = useRef<(() => void) | null>(null);

  // Reverse kick state used by classes/UI
  const [isReverseStarted, setIsReverseStarted] = useState(false);

  // Other states
  const [isScaleUpComplete, setIsScaleUpComplete] = useState(false);
  const [isModalExiting, setIsModalExiting] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const isModalOpenRef = useRef(false);
  const isOpeningModalFromRoute = useRef(false);
  const isFromDeeplinkRef = useRef(false); // Track if user arrived via deeplink
  const [activeChannelIndex, setActiveChannelIndex] = useState<number>(0);

  // Deeplink detection - store channel and video slugs when arriving via URL
  const pendingDeeplinkRef = useRef<{ channelSlug: string; videoSlug: string } | null>(null);
  const hasCheckedDeeplinkRef = useRef(false); // Track if we've already validated the deeplink once

  // Reverse kick gate - settling timer before activating gesture detection
  const reverseSettleTimeoutRef = useRef<number | null>(null);

  // --------------------
  // Mode detection: Portrait/Landscape & Touch/Non-Touch
  //#region Mode detection

  // ORIENTATION MODES:
  //  - Landscape: width >= height (default) - Channel items are 16:9, sized to fit 1.5 items onscreen
  //  - Portrait: height > width - Channel items are 9:16, height is 40% of viewport height

  // Update orientation on resize
  useEffect(() => {
    // Don't add window listeners until client is mounted
    if (!mounted) return;

    // Initialize orientation on mount
    const newIsPortrait = getIsPortrait();
    setIsPortraitMode(newIsPortrait);

    const checkOrientation = () => {
      const newIsPortrait = getIsPortrait();
      if (newIsPortrait !== isPortraitMode) {
        log('Orientation changed', { from: isPortraitMode ? 'portrait' : 'landscape', to: newIsPortrait ? 'portrait' : 'landscape' });
        setIsPortraitMode(newIsPortrait);

        // Show loading overlay during orientation change, animations will reset isAnimationsReady
        setShowLoadingOverlay(true);
        setIsAnimationsReady(false);
      }
    };

    // Listen for resize and orientation changes
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [mounted, isPortraitMode]);

  // DEBUG: Log CONFIG
  useEffect(() => {
    // Don't log until showIntro has been determined
    if (showIntro === null) return;

    log('CONFIG:', {
      'Touch Device': isTouchDeviceState,
      'Show Intro': showIntro,
      'Is Desktop': isDesktop,
      'Orientation': isPortraitMode ? 'Portrait' : 'Landscape',
      'Viewport': `${window.innerWidth}×${window.innerHeight}`,
    });
  }, [isTouchDeviceState, showIntro, isDesktop, isPortraitMode]);

  //#endregion Mode detection
  // -------------------- Channel click
  //#region Channel click

  const handleChannelClick = useCallback((channel: RockHallTvChannel, channelIndex: number) => {
    log('Channel clicked', { channel, channelIndex });

    setActiveChannelIndex(channelIndex);

    const videos = ensurePostsAreRHTVVideos(channel.rhtvChannelContent?.rhtvVideos);
    const firstVideo = videos?.[0];

    if (firstVideo) {
      setActiveChannel(channel);
      setActiveVideo(firstVideo);
      setIsModalExiting(false); // Ensure we're not in exiting state
      setShowModal(true);

      // Update route with channel and video slugs
      updateRHTVRoute({ channelSlug: channel.slug, videoSlug: firstVideo.slug }); // (ROUTE-CHANGE)

    }
  }, [setActiveChannel, setActiveVideo, setShowModal, updateRHTVRoute]);

  // Handler for when a channel item receives keyboard focus
  const handleChannelFocus = useCallback((channelIndex: number, channelElement: HTMLElement) => {
    // Only scroll into view if kick is complete and modal is not open
    if (!isKickComplete || showModal || !channelsRowRef.current || !stSlideRef.current) return;

    const row = channelsRowRef.current;
    const viewport = channelsViewportRef.current;
    if (!viewport) return;

    // Get viewport dimensions
    const viewportRect = viewport.getBoundingClientRect();
    const viewportWidth = viewportRect.width;

    // Get channel position relative to the row
    const channelLeft = channelElement.offsetLeft;
    const channelWidth = channelElement.offsetWidth;
    const channelCenter = channelLeft + (channelWidth / 2);

    // Current row position
    const currentX = gsap.getProperty(row, 'x') as number;

    // Calculate desired position to center the channel in viewport
    const targetX = (viewportWidth / 2) - channelCenter;

    // Get ScrollTrigger bounds
    const scrollTrigger = stSlideRef.current;
    const maxScroll = scrollTrigger.end - scrollTrigger.start;

    // Clamp target position to valid bounds
    const clampedX = Math.max(-maxScroll, Math.min(0, targetX));

    // Only animate if position actually changes
    if (Math.abs(currentX - clampedX) > 1) {
      // log('Scrolling to focused channel', { channelIndex, targetX: clampedX });

      // Animate the row position
      gsap.to(row, {
        x: clampedX,
        duration: 0.6,
        ease: 'power2.out',
        onUpdate: () => {
          // Sync ScrollTrigger progress with drag position
          const newX = gsap.getProperty(row, 'x') as number;
          const progress = Math.abs(newX / maxScroll);
          scrollTrigger.scroll(scrollTrigger.start + (progress * maxScroll));
        }
      });
    }
  }, [isKickComplete, showModal]);

  //#endregion Channel click
  // -------------------- Scale Up/Down
  //#region Scale Up/Down

  // Store the original x position before scale up
  const originalXPositionRef = useRef<number>(0);

  // Store the actual modal content dimensions and position
  const modalContentSizeRef = useRef<{ width: number; height: number; x: number; y: number } | null>(null);

  // Track channel item target size (matches modal player size)
  const [channelItemTargetSize, setChannelItemTargetSize] = useState<{ width: number; height: number } | null>(null);

  // Calculates the target size for channel items based on orientation mode.
  const calculateChannelItemTargetSize = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const currentIsPortrait = getIsPortrait();

    // Calculate responsive header buffer once (used for both orientations)
    const aspectRatio = viewportWidth / viewportHeight;
    let headerBufferPercent: number;

    if (aspectRatio >= CHANNEL_LAYOUT.ASPECT_VERY_WIDE) {
      // Very wide screens (like 1800x900 = 2.0): minimal buffer
      headerBufferPercent = CHANNEL_LAYOUT.HEADER_BUFFER_MINIMAL;
    } else if (aspectRatio >= CHANNEL_LAYOUT.WIDE_LANDSCAPE_THRESHOLD) {
      // Wide landscape screens: reduced buffer
      headerBufferPercent = CHANNEL_LAYOUT.HEADER_BUFFER_REDUCED;
    } else {
      // Standard/square screens: original buffer
      headerBufferPercent = CHANNEL_LAYOUT.HEADER_BUFFER_STANDARD;
    }

    // Apply buffer to available height (single calculation for both orientations)
    let availableHeight = heightChannelRow();
    const headerBuffer = headerBufferPercent * viewportHeight;
    availableHeight = Math.max(0, availableHeight - headerBuffer);

    let width: number;
    let height: number;

    if (currentIsPortrait) {
      // PORTRAIT MODE: 9:16 aspect ratio
      // Strategy: Try width-first (1.5 items), but ensure height doesn't exceed available space

      // Try width-based sizing first (1.5 items onscreen)
      const widthBasedWidth = Math.ceil(viewportWidth / CHANNEL_LAYOUT.ITEMS_PER_SCREEN_DEFAULT);
      const widthBasedHeight = Math.ceil(widthBasedWidth / CHANNEL_LAYOUT.ASPECT_PORTRAIT);

      // Check if width-based height fits in available space
      if (widthBasedHeight <= availableHeight) {
        // Width-based sizing fits! Use it.
        width = widthBasedWidth;
        height = widthBasedHeight;
      } else {
        // Width-based sizing is too tall, switch to height-based sizing
        // Use available height and calculate width from that
        height = availableHeight;
        width = Math.ceil(height * CHANNEL_LAYOUT.ASPECT_PORTRAIT);
      }
    } else {
      // LANDSCAPE MODE: 16:9 aspect ratio, width sized for 1.5 items onscreen
      // Strategy: Try width-first (1.5 items), but ensure height doesn't exceed available space

      // Try width-based sizing first - adjust items per screen for very wide displays
      let itemsPerScreen: number = CHANNEL_LAYOUT.ITEMS_PER_SCREEN_DEFAULT; // Default
      if (aspectRatio >= CHANNEL_LAYOUT.ASPECT_VERY_WIDE) {
        // Very wide screens (like 1800x900): show more items to reduce individual size
        itemsPerScreen = CHANNEL_LAYOUT.ITEMS_PER_SCREEN_VERY_WIDE; // Smaller items, less gap
      } else if (aspectRatio >= CHANNEL_LAYOUT.ASPECT_WIDE) {
        // Wide screens: moderate adjustment
        itemsPerScreen = CHANNEL_LAYOUT.ITEMS_PER_SCREEN_WIDE;
      }

      const widthBasedWidth = Math.ceil(viewportWidth / itemsPerScreen);
      const widthBasedHeight = Math.ceil(widthBasedWidth / CHANNEL_LAYOUT.ASPECT_LANDSCAPE);

      // Always check if width-based sizing fits in available height
      // Even for very wide screens, we must respect the header space
      if (widthBasedHeight <= availableHeight) {
        // Width-based sizing fits! Use it.
        width = widthBasedWidth;
        height = widthBasedHeight;
      } else {
        // Width-based sizing is too tall, switch to height-based sizing
        // Use available height and calculate width from that
        height = availableHeight;
        width = Math.ceil(height * CHANNEL_LAYOUT.ASPECT_LANDSCAPE);
      }
    }

    return { width, height };
  }, [heightChannelRow]);

  // Calculate channel item size on mount and window resize
  useEffect(() => {
    // Don't run until client is mounted and we have access to window
    if (!mounted) return;

    const updateChannelItemSize = () => {
      const targetSize = calculateChannelItemTargetSize();
      setChannelItemTargetSize(targetSize);
      // log('Channel item target size:', targetSize);

      // After resize, lock channel title widths to their natural dimensions
      // This prevents text reflow during scale animations
      if (channelsRowRef.current) {
        const channelTitles = channelsRowRef.current.querySelectorAll('.channel-title');
        channelTitles.forEach((title) => {
          const titleEl = title as HTMLElement;
          // First, clear any existing inline width to get natural width
          titleEl.style.width = '';
        });
      }
    };

    // Calculate on mount
    updateChannelItemSize();

    // Recalculate on resize (debounced via the function's memoization)
    window.addEventListener('resize', updateChannelItemSize);

    return () => {
      window.removeEventListener('resize', updateChannelItemSize);
    };
  }, [mounted, calculateChannelItemTargetSize]);

  // When channelItemTargetSize changes, update ScrollTrigger's slide timeline
  // This ensures the scroll distance matches the new item dimensions
  useEffect(() => {
    // Only update if kick is complete and we have a ScrollTrigger instance
    if (!isKickComplete || !stSlideRef.current || !channelItemTargetSize) return;

    // Rebuild the slide timeline with new dimensions
    requestAnimationFrame(() => {
      if (stSlideRef.current) {
        const rowEl = channelsRowRef.current;
        const vpEl = channelsViewportRef.current;

        if (rowEl && vpEl) {
          const scrollWidth = rowEl.scrollWidth;
          const viewportWidth = vpEl.clientWidth;
          const pxSlide = Math.max(0, Math.ceil(scrollWidth - viewportWidth));

          // Update the ScrollTrigger's end value
          stSlideRef.current.vars.end = `+=${pxSlide}`;
          stSlideRef.current.refresh();
        }
      }
    });
  }, [channelItemTargetSize, isKickComplete]);

  // Execute scale up animation
  const executeScaleUp = useCallback((modalDimensions: { width: number; height: number; x: number; y: number }) => {
    // Touch devices: Skip scale animation entirely (just fade modal in)
    if (isTouchDeviceState) {
      log('Touch device: Skipping scale animation, using fade only');
      setIsScaleUpComplete(true);
      setIsScaling(false);
      return;
    }

    // Non-touch devices: Execute scale animation
    // log('executeScaleUp - scaling channels to match modal size and position', modalDimensions);

    // Store the current x position before we start scaling up
    const currentX = gsap.getProperty(channelsRowRef.current, 'x') as number;
    originalXPositionRef.current = currentX;
    // log('Storing original x position', currentX);

    // Mark that we're currently scaling
    setIsScaling(true);

    // Calculate scale needed to make channel items match modal player height
    // Get the actual channel item height (not the container height)
    const channelElements = channelsRowRef.current?.querySelectorAll('.channel-item');
    if (!channelElements || channelElements.length === 0) return;

    const firstChannelItem = channelElements[0] as HTMLElement;
    const currentChannelItemHeight = firstChannelItem.offsetHeight;

    // Scale = target height / current height
    const targetScale = modalDimensions.height / currentChannelItemHeight;

    // Create timeline to scale up channels row
    const scaleUpTl = gsap.timeline({
      defaults: { ease: ANIMATION_CONFIG.EASE_SCALE_UP, duration: ANIMATION_CONFIG.DUR_SCALE },
      onComplete: () => {
        setIsScaleUpComplete(true);
        // Keep isScaling true - channels stay non-interactive while modal is open
      }
    }).add('start');

    // Channels scale up
    scaleUpTl.to(channelsRowRef.current, {
      scale: targetScale,
    }, 'start');

    // Calculate horizontal position to center active channel with modal
    if (channelElements && activeChannel !== null) {
      const activeChannelEl = channelElements[activeChannelIndex] as HTMLElement;

      // In portrait mode, account for the width expansion when calculating center position
      const currentChannelWidth = activeChannelEl.offsetWidth;
      let finalChannelWidth = currentChannelWidth;

      if (isPortraitMode) {
        // In portrait mode, the channel will expand to match player width (divided by scale)
        finalChannelWidth = modalDimensions.width / targetScale;
      }

      // Center of the active channel within the row (using FINAL width after expansion)
      const activeChannelLeft = activeChannelEl.offsetLeft;
      const activeChannelCenter = activeChannelLeft + (finalChannelWidth / 2);

      // Center of the modal on screen
      const modalCenterX = modalDimensions.x + (modalDimensions.width / 2);

      // Calculate target X: position the scaled channel center at modal center
      // Formula: modalCenter - (channelCenter * scale)
      const targetX = modalCenterX - (activeChannelCenter * targetScale);

      scaleUpTl.to(channelsRowRef.current, {
        x: targetX,
      }, 'start');
    }

    // Get the current position of a channel item (before scaling)
    const channelItemForPositioning = channelElements[0] as HTMLElement;
    const channelItemRect = channelItemForPositioning.getBoundingClientRect();
    const rowRect = channelsRowRef.current.getBoundingClientRect();

    // Modal player position
    const modalTop = modalDimensions.y;
    const modalBottom = modalDimensions.y + modalDimensions.height;

    // Current channel item bottom position (this is what stays anchored during scale)
    const currentChannelBottom = channelItemRect.bottom;

    // Distance from row bottom to channel item bottom
    const channelOffsetFromRowBottom = rowRect.bottom - channelItemRect.bottom;

    // After scaling, this offset will also scale
    const scaledChannelOffsetFromRowBottom = channelOffsetFromRowBottom * targetScale;

    // We want the scaled channel bottom to align with modal bottom
    // Since transform-origin is bottom, row bottom stays at current Y
    // We need to calculate where row bottom should be so scaled channel aligns with modal
    const targetRowBottom = modalBottom + scaledChannelOffsetFromRowBottom;
    const targetY = targetRowBottom - rowRect.bottom;

    scaleUpTl.to(channelsRowRef.current, {
      y: targetY,
    }, 'start');

    // All ChannelItems' .channel-titles fade out
    scaleUpTl.to(channelsRowRef.current?.querySelectorAll('.channel-title'), {
      opacity: 0,
    }, 'start');

    // Fade out non-active channel items
    if (channelElements && activeChannelIndex !== null) {
      const nonActiveChannels = Array.from(channelElements).filter((_, index) => index !== activeChannelIndex);
      scaleUpTl.to(nonActiveChannels, {
        opacity: 0,
      }, 'start');
    }

    // Portrait mode: Animate active channel width to match player width
    if (isPortraitMode && channelElements && activeChannelIndex !== null) {
      const activeChannelEl = channelElements[activeChannelIndex] as HTMLElement;

      // Calculate target width: modal width divided by scale (since row is scaling)
      const targetWidth = modalDimensions.width / targetScale;

      scaleUpTl.to(activeChannelEl, {
        width: targetWidth,
      }, 'start');
    }

    // Fade in the dark overlay
    scaleUpTl.to(overlayRef.current, {
      opacity: 1,
    }, 'start');

    if (tlScaleUpRef.current) tlScaleUpRef.current.kill();
    tlScaleUpRef.current = scaleUpTl;

  }, [activeChannel, activeChannelIndex, isPortraitMode, isTouchDeviceState]);

  // Callback to receive modal content dimensions and position
  const handleModalContentSizeChange = useCallback((dimensions: { width: number; height: number; x: number; y: number }) => {
    // log('Modal content dimensions and position received:', dimensions);
    modalContentSizeRef.current = dimensions;

    // If we're waiting to scale up, do it now that we have the dimensions
    if (showModal && !isScaleUpComplete) {
      executeScaleUp(dimensions);
    }
  }, [showModal, isScaleUpComplete, executeScaleUp]);

  // Execute scale down animation
  const handleScaleDown = useCallback(() => {
    // Touch devices: Skip scale animation entirely (modal just faded out)
    if (isTouchDeviceState) {
      log('Touch device: Skipping scale down animation, using fade only');
      setIsScaleUpComplete(false);
      setIsScaling(false);
      isOpeningModalFromRoute.current = false;
      return;
    }

    // Non-touch devices: Execute scale down animation
    // log('handleScaleDown - reversing scale up animation', { originalX: originalXPositionRef.current });

    // Show the channel row immediately before scale down starts
    if (channelsRowRef.current) {
      // log('Showing channel row for scale down');
      channelsRowRef.current.style.visibility = 'visible';
    }

    // Create reverse timeline to scale down channels row
    const scaleDownTl = gsap.timeline({
      defaults: { ease: ANIMATION_CONFIG.EASE_SCALE_DOWN, duration: ANIMATION_CONFIG.DUR_SCALE },
      onComplete: () => {
        // log('Scale down complete - re-enabling scroll');

        // Re-enable scroll after scale down completes (non-touch devices only)
        if (isKickCompleteRef.current && !isTouchDeviceState) {
          stSlideRef.current?.enable(false);
          bodyScrollUnlock();

          // Only unpause Lenis if it's running (not on touch devices)
          if (!DEV_DISABLE_LENIS) unpauseLenis();
        }

        setIsScaleUpComplete(false);
        setIsScaling(false); // Scale down is complete - channels are now interactive again
        isOpeningModalFromRoute.current = false;

        // Note: Reverse kick is NOT triggered here
        // It should only be triggered by user gesture (scroll to top + scroll up)
      }
    }).add('start');

    // Channels scale back down to normal
    scaleDownTl.to(channelsRowRef.current, {
      scale: 1,
      x: originalXPositionRef.current, // Return to the original scroll position
      y: 0, // Return to original vertical position
    }, 'start');

    // ChannelItems' .channel-titles fade back in
    scaleDownTl.to(channelsRowRef.current?.querySelectorAll('.channel-title'), {
      opacity: 1,
    }, 'start');

    // Fade all channel items back in (including non-active ones that were faded out)
    scaleDownTl.to(channelsRowRef.current?.querySelectorAll('.channel-item'), {
      opacity: 1,
    }, 'start');

    // Portrait mode: Reset active channel width back to original
    if (isPortraitMode && activeChannelIndex !== null && channelsRowRef.current) {
      const channelElements = channelsRowRef.current.querySelectorAll('.channel-item');
      const activeChannelEl = channelElements[activeChannelIndex] as HTMLElement;

      if (activeChannelEl && channelItemTargetSize) {
        scaleDownTl.to(activeChannelEl, {
          width: channelItemTargetSize.width,
        }, 'start');
      }
    }

    // Fade out the dark overlay
    scaleDownTl.to(overlayRef.current, {
      opacity: 0,
    }, 'start');

    // Kill any existing scale up timeline
    if (tlScaleUpRef.current) tlScaleUpRef.current.kill();

    // Set the new timeline
    tlScaleUpRef.current = scaleDownTl;

  }, [DEV_DISABLE_LENIS, isTouchDeviceState, isPortraitMode, activeChannelIndex, channelItemTargetSize]);

  //#endregion Scale Up/Down
  // -------------------- Modal
  //#region Modal

  // Detect deeplink on page load - save it for later processing
  useEffect(() => {
    // Only check once, when all conditions are met
    if (activeChannel && activeVideo && router.isReady && !hasCheckedDeeplinkRef.current) {
      hasCheckedDeeplinkRef.current = true; // Mark as checked to prevent re-runs

      logGreen('🔗 DEEPLINK DETECTED', {
        channel: activeChannel.slug,
        video: activeVideo.slug
      });

      // TODO: Maybe later - Validate deeplink: check if channel exists in our channels array
      /* const channelIndex = channels.findIndex((ch) => ch.slug === activeChannel.slug);
      if (channelIndex === -1) {
        // Channel not found - clear invalid URL
        log('Invalid deeplink: Channel not found, clearing URL');
        updateRHTVRoute(); // Clear to base /rrtv/ URL
        return;
      } */

      // TODO: Maybe later - Validate deeplink: check if video exists in the channel
      /* const channel = channels[channelIndex];
      const videos = ensurePostsAreRHTVVideos(channel.rhtvChannelContent?.rhtvVideos);
      const videoExists = videos?.some(v => v.slug === activeVideo.slug);
      if (!videoExists) {
        // Video not found in channel - clear invalid URL
        log('Invalid deeplink: Video not found in channel, clearing URL');
        updateRHTVRoute(); // Clear to base /rrtv/ URL
        return;
      } */

      // Deeplink is valid - store for processing after kick intro
      logGreen('✅ Deeplink is valid - storing for later');
      pendingDeeplinkRef.current = {
        channelSlug: activeChannel.slug,
        videoSlug: activeVideo.slug
      };

      // Mark that we arrived via deeplink (for browser back button behavior)
      isFromDeeplinkRef.current = true;
    }
  }, [router.isReady]);

  // Modal show/hide
  useEffect(() => {
    // Skip all modal logic if we're aborting and reloading
    if (isAbortingReloadRef.current) {
      log('Modal effect skipped - aborting and reloading');
      return;
    }

    // SHOW the modal
    if (showModal && !isModalOpenRef.current) {

      log('Show modal');

      // Kill reverse gesture observer when modal opens
      reverseGestureObserverDestroyRef.current?.();

      // Clear any pending reverse settle timer
      if (reverseSettleTimeoutRef.current != null) {
        clearTimeout(reverseSettleTimeoutRef.current);
        reverseSettleTimeoutRef.current = null;
      }

      // On touch devices: Fade in the overlay directly (no scale animation)
      if (isTouchDeviceState && overlayRef.current) {
        gsap.to(overlayRef.current, {
          opacity: 1,
          duration: 0.30,
          ease: 'power2.out'
        });
      }

      // On non-touch devices: Disable ScrollTrigger and lock body scroll for scale animation
      // On touch devices: Lock body scroll to prevent background scrolling
      if (!isTouchDeviceState) {
        stSlideRef.current?.disable(false); // Disable without refresh
      }

      // Lock body scroll on all devices when modal is open
      bodyScrollLock();

      // Only pause Lenis if it's running (not on touch devices)
      if (!DEV_DISABLE_LENIS && !isTouchDeviceState) pauseLenis();
    }

    // HIDE the modal
    else if (!showModal && isModalOpenRef.current) {
      log('Modal hide initiated');

      // On touch devices: Fade out the overlay directly (no scale animation)
      if (isTouchDeviceState && overlayRef.current) {
        gsap.to(overlayRef.current, {
          opacity: 0,
          duration: 0.40,
          ease: 'power2.out'
        });
      }

      // Clear route when modal closes
      updateRHTVRoute(); // (ROUTE-CHANGE)
    }

    // Save latest modal state in ref
    isModalOpenRef.current = showModal;

  }, [showModal, DEV_DISABLE_LENIS, isTouchDeviceState]);

  // Modal close complete handler
  const handleModalCloseComplete = useCallback(() => {
    log('Modal close animation complete - starting scale down');
    setIsModalExiting(false); // Reset for next time
    setIsScaleUpComplete(false); // Reset for next time

    // Unlock body scroll when modal closes (all devices)
    bodyScrollUnlock();

    handleScaleDown();
  }, [handleScaleDown, bodyScrollUnlock]);

  //#endregion Modal
  // -------------------- Lenis
  //#region Lenis

  useEffect(() => {
    // Don't use Lenis on touch devices - they have native smooth scrolling
    if (DEV_DISABLE_LENIS || isTouchDeviceState) return;

    // Start Lenis with the derived params so smoothness is controlled by one knob
    const lenis = startLenis({
      duration: LENIS_CONFIG.DURATION,
      easing: LENIS_CONFIG.EASING,
      wheelMultiplier: LENIS_CONFIG.WHEEL_MULTIPLIER,
    });

    // Drive Lenis from GSAP ticker so it stays in sync with ScrollTrigger
    const tick = (time: number) => {
      try { lenis?.raf(time * 1000); } catch (e) { /* noop */ }
      try { (ScrollTrigger as any)?.update(); } catch (e) { /* noop */ }
    };

    gsap.ticker.add(tick);

    return () => {
      gsap.ticker.remove(tick);
      stopLenis();
    };
  }, [DEV_DISABLE_LENIS, isTouchDeviceState]);

  //#endregion Lenis
  // -------------------- GSAP

  useGSAP(() => {

    // CRITICAL: Don't initialize GSAP animations until we know the screen size
    // This prevents incorrect animation setup during SSR or before viewport is determined
    if (!isClientReady) {
      log('GSAP setup skipped - waiting for client ready (screen size unknown)');
      return;
    }

    log('GSAP setup starting - client is ready', { isDesktop, isTouchDeviceState, showIntro });
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, Draggable);

    const section = rootEl.current!;
    const header = headerRef.current!;
    const row = channelsRowRef.current!;
    const vp = channelsViewportRef.current!;
    const scrollCta = scrollCtaRef.current; // Allow null when intro is not shown
    if (!section || !header || !row || !vp) return;

    // Set element transform origins (Important to do asap)
    gsap.set(row, { transformOrigin: 'left bottom', y: 0 });
    gsap.set(headerTextRef.current, { transformOrigin: 'left bottom' });
    gsap.set(logoRef.current, { transformOrigin: 'right bottom' });

    // --------------------
    // CTA animation (only if showing intro)

    const circle = scrollCtaCircleRef.current;
    if (showIntro && circle) {
      const dur = 0.3;

      const ctaTl = gsap.timeline({ repeat: -1 });
      ctaTl.to(circle, { y: 2, scaleY: 1.5, duration: dur, ease: 'power1.inOut' });
      ctaTl.to(circle, { y: 0, scaleY: 1, duration: dur * 2, ease: 'power1.inOut' });

      scrollCtaAnimRef.current = ctaTl;
    }

    // --------------------
    // Helpers

    // Rebuild slide TL
    function buildSlideTimeline() {
      const rowEl = channelsRowRef.current;
      const vpEl  = channelsViewportRef.current;
      if (!rowEl || !vpEl) return 0;
      const scrollWidth = rowEl.scrollWidth;
      const viewportWidth = vpEl.clientWidth;
      const pxSlide = Math.max(0, Math.ceil(scrollWidth - viewportWidth));

      if (tlSlideRef.current) tlSlideRef.current.kill();
      tlSlideRef.current = gsap.timeline({ paused: true, defaults: { ease: "none" }})
        .add('start')
        .to(rowEl, { x: -pxSlide, duration: pxSlide }, 'start')
        .addLabel('kermit', pxSlide / 2);

      tlSlideRef.current.pause(0).progress(0);
      return pxSlide;
    }

    // Trigger reverse kick animation
    function createSlideScrollTrigger(pxSlide: number) {
      if (stSlideRef.current) {
        stSlideRef.current.kill(true);
        stSlideRef.current = null;
      }

      stSlideRef.current = ScrollTrigger.create({
        id: 'rhtv-slide',
        trigger: section,
        start: 'top top',
        end: `+=${pxSlide}`,
        animation: tlSlideRef.current!,
        scrub: true,
        pin: true,
        anticipatePin: 1,
        pinSpacing: true,
        invalidateOnRefresh: true,
        onUpdate: (self: ScrollTrigger) => {
          // Reverse gesture detection logic
          const AT_START = self.progress <= SCROLLTRIGGER_CONFIG.AT_START_THRESHOLD;

          if (AT_START && isKickCompleteRef.current && !isReverseStartedRef.current) {
            // User is at top - clear any existing settle timer
            if (reverseSettleTimeoutRef.current != null) {
              clearTimeout(reverseSettleTimeoutRef.current);
              reverseSettleTimeoutRef.current = null;
            }

            // Start settle timer - if user stays at top for 500ms, create the gesture observer
            reverseSettleTimeoutRef.current = window.setTimeout(() => {
              reverseSettleTimeoutRef.current = null;

              // Double-check we're still at top and ready for reverse (desktop only)
              if (showIntro && isKickCompleteRef.current && !isReverseStartedRef.current) {
                log('User settled at top - creating reverse gesture observer');
                reverseGestureObserverCreate();
              } else if (!showIntro) {
                log('Skipping reverse gesture observer - not showing intro (tablet/mobile)');
              }
            }, SCROLLTRIGGER_CONFIG.REVERSE_SETTLE_TIME_MS);
          } else {
            // User scrolled away from start - clear settle timer and kill any active observer
            if (reverseSettleTimeoutRef.current != null) {
              clearTimeout(reverseSettleTimeoutRef.current);
              reverseSettleTimeoutRef.current = null;
            }

            // Kill the gesture observer if it exists
            reverseGestureObserverDestroy();
          }
        },
      });

      // start disabled by default; Kick will enable → refresh
      stSlideRef.current.disable(false);
    }

    // Create Draggable for horizontal dragging of channels row
    function createDraggable(pxSlide: number) {
      // Kill existing draggable if it exists
      if (draggableRef.current) {
        draggableRef.current.kill();
        draggableRef.current = null;
      }

      const row = channelsRowRef.current;
      if (!row) return;

      // Create Draggable instance
      const draggableInstance = Draggable.create(row, {
        type: 'x',
        trigger: row,
        inertia: false,
        bounds: { minX: -pxSlide, maxX: 0 },
        dragClickables: true, // Allow clicking/interacting with child elements
        allowEventDefault: false, // Prevent GSAP from forcing touch-action styles on children
        onDragEnd: function() {
          // Sync after drag ends
          if (tlSlideRef.current && stSlideRef.current) {
            const progress = -this.x / pxSlide;
            const scrollPos = stSlideRef.current.start + (progress * pxSlide);

            // Set flag to prevent circular updates
            isSyncingFromDraggableRef.current = true;

            tlSlideRef.current.progress(progress);
            window.scrollTo(0, scrollPos);

            // Clear flag after a brief delay to allow scroll to settle
            requestAnimationFrame(() => {
              isSyncingFromDraggableRef.current = false;
            });
          }
        },
      })[0];

      draggableRef.current = draggableInstance;

      // Update the ScrollTrigger's onUpdate to include draggable sync
      // This will be called for both scroll and draggable updates
      if (stSlideRef.current) {
        // Store the original onUpdate (reverse gesture detection)
        const originalOnUpdate = stSlideRef.current.vars.onUpdate;

        // Create new onUpdate that handles both draggable sync AND reverse gesture
        stSlideRef.current.vars.onUpdate = function(self: ScrollTrigger) {
          // Do not update if we're syncing from draggable
          if (!isSyncingFromDraggableRef.current) {
            // Only update draggable position if we're not currently dragging
            if (draggableInstance && !draggableInstance.isDragging) {
              const targetX = -self.progress * pxSlide;
              gsap.set(row, { x: targetX });
              draggableInstance.update(); // Update draggable's internal position
            }
          }

          // Call the original onUpdate (reverse gesture detection logic)
          if (typeof originalOnUpdate === 'function') {
            originalOnUpdate.call(this, self);
          }
        };
      }
    }

    // Helper to clear settle timer and kill reverse gesture observer
    function disarmReverseGate() {
      // Clear settle timer
      if (reverseSettleTimeoutRef.current != null) {
        clearTimeout(reverseSettleTimeoutRef.current);
        reverseSettleTimeoutRef.current = null;
      }

      // Kill gesture observer
      reverseGestureObserverDestroy();
    }

    // --------------------
    // EVERYTHING HAS BEEN INITIALIZED

    const everythingIsReady = () => {
      logGreen('everythingIsReady called', pendingDeeplinkRef.current);

      // Check if we need to process a pending deeplink now that everything is ready
      if (pendingDeeplinkRef.current) {// && router.isReady) {
        const { channelSlug, videoSlug } = pendingDeeplinkRef.current;
        log('Processing PENDING DEEPLINK now that GSAP is ready', { channelSlug, videoSlug });

        // Find the index of the channel
        const channelIndex = channels.findIndex((ch) => ch.slug === channelSlug);
        if (channelIndex !== -1) {
          setActiveChannelIndex(channelIndex);
          log('Set active channel index for pending deeplink:', channelIndex);
        }

        // TOUCH DEVICES: Skip kick animation and observers (already in "kicked" state, no observers exist)
        // NON-TOUCH DEVICES: Fast-forward kick animation and disable observers
        if (!isTouchDeviceState) {
          // Complete kick animation immediately if not already done
          if (!isKickComplete) {
            log('Pending deeplink: Fast-forwarding Kick animation to completion');
            tlKickRef.current?.progress(1);
            setIsKickComplete(true);
            isKickCompleteRef.current = true;
            setIsKickStarted(true);
            isKickStartedRef.current = true;
          }

          // Disable any active observers
          if (firstScrollObserver) {
            firstScrollObserver.kill();
            firstScrollObserver = null;
          }
          reverseGestureObserverDestroy();
        }

        // Scroll the target channel into view instantly before opening modal
        if (channelIndex !== -1 && channelsRowRef.current && channelItemTargetSize) {
          const channelElements = channelsRowRef.current.querySelectorAll('.channel-item');
          const targetChannelEl = channelElements[channelIndex] as HTMLElement;

          if (targetChannelEl) {
            if (isTouchDeviceState) {
              // TOUCH DEVICE: Use native scrollIntoView
              log('📍 Touch device: Scrolling channel into view (native)', { channelIndex });
              targetChannelEl.scrollIntoView({
                behavior: 'instant' as ScrollBehavior,
                block: 'nearest',
                inline: 'center'
              });
            } else if (stSlideRef.current) {
              // DESKTOP: Use ScrollTrigger-based positioning
              // Calculate the scroll position to center the target channel
              const channelLeft = targetChannelEl.offsetLeft;
              const channelWidth = channelItemTargetSize.width;
              const viewportWidth = window.innerWidth;

              // Center the channel in the viewport
              const targetScrollX = channelLeft - (viewportWidth / 2) + (channelWidth / 2);

              // Instantly scroll to the target channel position
              window.scrollTo({
                top: stSlideRef.current.start + targetScrollX,
                behavior: 'instant' as ScrollBehavior
              });
              log('📍 Desktop: Scrolled to center deeplink channel');
            }
          }
        }

        // Open the deeplinked channel and video
        // At this point, we know the deeplink is valid (validated during detection)
        const channel = channels[channelIndex];
        if (channel) {
          logGreen('✅ Opening deeplink channel and video', {
            channel: channel.slug,
            video: videoSlug,
            channelIndex
          });

          // Set the channel index
          setActiveChannelIndex(channelIndex);

          // Find the specific video by slug
          const videos = ensurePostsAreRHTVVideos(channel.rhtvChannelContent?.rhtvVideos);
          const targetVideo = videos?.find(v => v.slug === videoSlug);

          if (targetVideo) {
            // Set the channel, video, and show modal
            setActiveChannel(channel);
            setActiveVideo(targetVideo); // This will also update the route via context
            setIsModalExiting(false);
            setShowModal(true);
          } else {
            // Fallback to first video if slug not found
            log('⚠️ Video slug not found, falling back to first video');
            handleChannelClick(channel, channelIndex);
          }
        }

        // Clear the pending deeplink
        pendingDeeplinkRef.current = null;

        // Clear the isFromDeeplink flag after processing
        isFromDeeplinkRef.current = false;

        log('✅ Deeplink processing complete');
      }
    };

    // --------------------

    // Handle resize - reinitialize animations
    const onResize = () => {
      logGreen('onResize - destroying and reinitializing animations');

      // Skip resize logic if aborting and reloading
      if (isAbortingReloadRef.current) {
        log('onResize skipped - aborting and reloading');
        return;
      }

      if (isModalOpenRef.current) return;

      // Use requestAnimationFrame to ensure DOM has updated with new styles
      requestAnimationFrame(() => {
        cleanupAnimations();
        initAnimations();
        // Force ScrollTrigger to refresh and recalculate everything
        ScrollTrigger.refresh(true);

        // Mark animations as ready after reinit completes
        setIsAnimationsReady(true);

        // log('GSAP animations reinitialized and ready');
        everythingIsReady();
      });
    };

    // Kick off animation
    const createKickAnimation = (pxKick: number, durKick: number) => {
      // Create timeline
      const tlKick = gsap.timeline({
        paused: true,
        defaults: { ease: ANIMATION_CONFIG.EASE_KICK, duration: durKick, overwrite: 'auto' },
        onComplete: () => {
          // log('tlKick onComplete');

          setIsKickComplete(true);
          isKickCompleteRef.current = true;

          // Rebuild slide TL + ST with the post-Kick geometry (only for non-touch devices)
          const pxSlide = buildSlideTimeline();
          if (!DEV_FORCE_TOUCH_DEVICE && !isTouchDeviceState) {
            createSlideScrollTrigger(pxSlide);

            // Enable + refresh now that Kick's layout is final
            stSlideRef.current!.enable(false);
            stSlideRef.current!.refresh();
            ScrollTrigger.refresh();
          }

          // Disarm reverse gate when kick completes (clears any timers/observers)
          disarmReverseGate();

          // Clear GSAP inline styles from channel titles (keeps them responsive)
          gsap.set(row.querySelectorAll('.channel-title'), { clearProps: 'transform,translate,rotate,scale' });
        }
      });
      tlKick.add('start');

      // CTA animates out (only if showing intro)
      if (scrollCta) {
        tlKick.fromTo(scrollCta, {
          y: 0,
          opacity: 1,
        }, {
          y: -pxKick / 4,
          opacity: 0,
        }, 'start');
      }

      // ~ ~ ~ ~ ~ ~ ~ ~ ~ ~
      // Header / logo / text animations

      if (showIntro) {

        // Header scales down and moves up slightly
        tlKick.to(header, {
          y: -pxKick * CHANNEL_LAYOUT.HEADER_MOVE_MULTIPLIER,
        }, 'start');

        // Header text scales down to 0.75 using transform origin left top
        tlKick.to(headerTextRef.current, {
          scale: 0.75,
        }, 'start');

        // Logo scales down to 0.75 using transform origin right top
        tlKick.to(logoRef.current, {
          scale: 0.75,
        }, 'start');

      }

      // ~ ~ ~ ~ ~ ~ ~ ~ ~ ~

      // Channel Row scales up
      tlKick.fromTo(row, { scale: CHANNEL_LAYOUT.PRE_KICK_SCALE }, { scale: 1 }, 'start');

      // Ensure Y position is at default (0) after kick completes
      tlKick.set(row, { y: 0 }, 'start');

      // ChannelItems' .channel-titles fade in
      tlKick.fromTo(row.querySelectorAll('.channel-title'), {
        opacity: 0,
        y: ANIMATION_CONFIG.CHANNEL_TITLES_DOWN,
      }, {
        opacity: 1,
        y: 0
      }, 'start');

      return tlKick;
    };

    // Reverse kick off animation
    const createReverseAnimation = () => {
      log('Creating reverse kick animation');

      const reverseDur = 1.5;

      // Create timeline
      const tlReverse = gsap.timeline({
        paused: true,
        defaults: {
          duration: reverseDur,
          ease: ANIMATION_CONFIG.EASE_KICK,
          overwrite: 'auto'
        },
        onStart: () => {
          log('Reverse kick animation STARTED');

          // Mark reverse as started
          setIsReverseStarted(true);
          isReverseStartedRef.current = true;

          // Disarm reverse gate when reverse starts
          disarmReverseGate();

          // Kill ST completely so no pin/spacing lingers during reverse
          if (stSlideRef.current) {
            stSlideRef.current.kill(true);
            stSlideRef.current = null;
          }
          tlSlideRef.current?.pause(0).progress(0);
        },
        onComplete: () => {
          log('Reverse kick animation COMPLETED');

          // Reset forward-kick state variables to initial
          isKickStartedRef.current = false;
          isKickCompleteRef.current = false;
          setIsKickStarted(false);
          setIsKickComplete(false);

          // Clear reverse started flags
          isReverseStartedRef.current = false;
          setIsReverseStarted(false);

          // Reset the Kick timeline so it can play again
          if (tlKickRef.current) {
            tlKickRef.current.pause(0).progress(0).invalidate();
          }

          // Disarm reverse gate when reverse completes
          disarmReverseGate();

          // Re-enable the first scroll observer so user can kick again (desktop only)
          if (showIntro) {
            firstScrollObserverCreate();
          }

          log('Reverse kick complete - returned to initial state');
        }
      });
      tlReverse.add('start');

      // 1. Scale channels row back down to 0.32 (original pre-kick size)
      tlReverse.to(channelsRowRef.current, {
        scale: CHANNEL_LAYOUT.PRE_KICK_SCALE,
        x: 0,
      }, 'start');

      // 2. ChannelItems' .channel-titles fade out
      tlReverse.to(channelsRowRef.current?.querySelectorAll('.channel-title'), {
        opacity: 0,
        y: ANIMATION_CONFIG.CHANNEL_TITLES_DOWN,
      }, 'start');

      // 3. Move header elements back down to original y (undo the kick move up)
      tlReverse.to(header, {
        y: 0,
      }, 'start');

      // 4. Scale header text back up to 1 (original size)
      tlReverse.to(headerTextRef.current, {
        scale: 1,
      }, 'start');

      // 5. Scale logo back up to 1 (original size)
      tlReverse.to(logoRef.current, {
        scale: 1,
      }, 'start');

      // 6. Bring the Scroll CTA back in (only if showing intro)
      if (scrollCta) {
        const ctaDur = 1.0;
        tlReverse.to(scrollCta, {
          y: 0,
          opacity: 1,
          duration: ctaDur,
          ease: ANIMATION_CONFIG.EASE_KICK
        }, 'start+=' + (reverseDur - ctaDur));
      }

      // 7. Fade out overlay
      tlReverse.to(overlayRef.current, {
        opacity: 0,
        duration: 1.0,
        ease: ANIMATION_CONFIG.EASE_KICK
      }, 'start');

      return tlReverse;
    };

    // Trigger the kick animation (can be called by scroll or keyboard focus)
    const triggerKickAnimation = () => {
      if (isKickStartedRef.current || isReverseStartedRef.current) return;

      // log('Triggering kick animation');
      isKickStartedRef.current = true;
      setIsKickStarted(true);

      // Kill the first scroll observer if it exists
      firstScrollObserverDestroy();

      // Keep ST out of the way during Kick; scrub TL at start
      if (stSlideRef.current) stSlideRef.current.disable(false);
      tlSlideRef.current?.pause(0).progress(0);

      // Disarm reverse gate when starting a new Kick
      disarmReverseGate();

      // Play the Kick animation
      if (tlKickRef.current) {
        // log('Kick animation timeline exists, playing now');
        tlKickRef.current.play(0);
      }
    };

    // Store the trigger function in a ref so it can be called from outside
    triggerKickCallbackRef.current = triggerKickAnimation;

    // -------------------- First Scroll Observer

    let firstScrollObserver: Observer | null = null;

    // Create the first scroll observer
    const firstScrollObserverCreate = () => {
      // Only create if not already started and not in reverse mode
      if (isKickStartedRef.current || isReverseStartedRef.current) {
        log('firstScrollObserverCreate skipped', {
          isKickStarted: isKickStartedRef.current,
          isReverseActive: isReverseStartedRef.current
        });
        return;
      }

      log('Creating first scroll observer');

      // Observer for wheel/touch/scroll events
      firstScrollObserver = Observer.create({
        target: window,
        type: 'wheel,touch,scroll',
        preventDefault: true,
        onChangeY() {
          triggerKickAnimation();
        }
      });

      // Keyboard event listener for arrow keys (since Observer doesn't work without scrollable content)
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageDown' || e.key === 'PageUp') {
          triggerKickAnimation();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      // Store cleanup function
      const originalKill = firstScrollObserver.kill.bind(firstScrollObserver);
      firstScrollObserver.kill = () => {
        window.removeEventListener('keydown', handleKeyDown);
        originalKill();
      };
    };

    // Destroy the first scroll observer
    const firstScrollObserverDestroy = () => {
      if (firstScrollObserver) {
        log('Destroying first scroll observer');
        firstScrollObserver.kill();
        firstScrollObserver = null;
      }
    };

    // -------------------- Reverse Gesture Observer

    // Destroy the reverse gesture observer
    const reverseGestureObserverDestroy = () => {
      if (reverseGestureObserverRef.current) {
        log('Destroying reverse gesture observer');
        reverseGestureObserverRef.current.kill();
        reverseGestureObserverRef.current = null;
      }
    };

    // Store the destroy function in a ref so it can be called from outside useGSAP
    reverseGestureObserverDestroyRef.current = reverseGestureObserverDestroy;

    // Create Observer for detecting deliberate upward scroll when at top
    const reverseGestureObserverCreate = () => {
      // Kill any existing observer first
      if (reverseGestureObserverRef.current) {
        reverseGestureObserverRef.current.kill();
      }

      log('Creating reverse gesture observer');

      reverseGestureObserverRef.current = Observer.create({
        target: window,
        type: 'wheel,touch',
        onUp() {
          // Only trigger if not already in reverse
          if (!isReverseStartedRef.current) {
            log('Deliberate upward scroll detected at top - triggering reverse');
            triggerReverse();
          }
        }
      });
    };

    // Trigger the reverse kick animation (desktop only)
    const triggerReverse = () => {
      // Skip reverse logic entirely if not showing intro (tablet/mobile)
      if (!showIntro) {
        log('triggerReverse skipped - not showing intro (tablet/mobile)');
        return;
      }

      if (isReverseStartedRef.current || !isKickCompleteRef.current) {
        log('triggerReverse skipped', {
          isReverseActive: isReverseStartedRef.current,
          isKickComplete: isKickCompleteRef.current
        });
        return;
      }

      log('Starting reverse kick animation');

      // Kill the reverse gesture observer - no longer needed
      reverseGestureObserverDestroy();

      // Set reverse kick state
      setIsReverseStarted(true);
      isReverseStartedRef.current = true;

      // Create and play reverse kick animation
      tlReverseRef.current = createReverseAnimation();
      tlReverseRef.current?.play();
    };

    // -------------------- Animation init/cleanup

    // Cleanup function to kill all animations and observers
    const cleanupAnimations = () => {
      logGreen('cleanupAnimations');

      // Kill Draggable first
      if (draggableRef.current) {
        draggableRef.current.kill();
        draggableRef.current = null;
      }

      // Kill ScrollTrigger (this should remove pin-spacer)
      if (stSlideRef.current) {
        stSlideRef.current.kill(true); // true = remove pin-spacer
        stSlideRef.current = null;
      }

      // Kill timelines
      if (tlKickRef.current) tlKickRef.current.kill();
      if (tlReverseRef.current) tlReverseRef.current.kill();
      if (tlSlideRef.current) tlSlideRef.current.kill();
      if (tlScaleUpRef.current) tlScaleUpRef.current.kill();

      tlKickRef.current = null;
      tlReverseRef.current = null;
      tlSlideRef.current = null;
      tlScaleUpRef.current = null;

      // Kill first scroll observer if it exists
      firstScrollObserverDestroy();

      // Kill reverse gesture observer if it exists
      reverseGestureObserverDestroy();

      // Clear any inline styles ScrollTrigger might have added to the section
      gsap.set(section, { clearProps: 'all' });

      // Disarm reverse gate when cleaning up
      disarmReverseGate();
    };

    // Initialize all animations
    const initAnimations = () => {

      logGreen('initAnimations', { isKickComplete: isKickCompleteRef.current, isKickStarted: isKickStartedRef.current });

      // --------------------
      // ScrollTrigger / Slide animation

      // Build slide timeline (always needed for kick animation state)
      const pxSlide = buildSlideTimeline();

      // Only create ScrollTrigger and Draggable on non-touch devices
      // Touch devices use native scrolling for the video tray instead
      if (!DEV_FORCE_TOUCH_DEVICE && !isTouchDeviceState) {
        createSlideScrollTrigger(pxSlide);

        // Create Draggable for horizontal drag (after ScrollTrigger is created)
        createDraggable(pxSlide);

        // Enable or disable based on whether kick is complete
        if (isKickCompleteRef.current) {
          stSlideRef.current!.enable(false);
          stSlideRef.current!.refresh();
        } else {
          stSlideRef.current!.disable(false);
        }
      }

      // --------------------
      // SETUP: Kick animation

      const pxKick = header.offsetHeight;
      const durKick = ANIMATION_CONFIG.DUR_KICK;

      tlKickRef.current = createKickAnimation(pxKick, durKick);

      // If kick was already completed, apply the end state immediately without animation
      if (isKickCompleteRef.current || isKickStartedRef.current) {
        logGreen('Kick already started/complete - applying end state immediately');

        // Apply all kick animation end states directly
        if (scrollCta) {
          gsap.set(scrollCta, { y: -pxKick / 4, opacity: 0 });
        }
        if (showIntro) {
          gsap.set(header, { y: -pxKick * CHANNEL_LAYOUT.HEADER_MOVE_MULTIPLIER });
          gsap.set(headerTextRef.current, { scale: 0.75 });
          gsap.set(logoRef.current, { scale: 0.75 });
        }
        gsap.set(row, { scale: 1, y: 0 }); // Ensure Y is at default position
        gsap.set(row.querySelectorAll('.channel-title'), { opacity: 1 });
        gsap.set(row.querySelectorAll('.channel-title'), { clearProps: 'transform,translate,rotate,scale' });

        // Mark timeline as complete without triggering callbacks
        // suppressEvents: true prevents onComplete from firing
        tlKickRef.current.progress(1, true); // true = suppress events
      }

      // Recreate first scroll observer if showing intro and kick hasn't started yet
      if (showIntro && !isKickStartedRef.current && !isReverseStartedRef.current) {
        log('Recreating first scroll observer after animations init');
        firstScrollObserverCreate();
      }

    };

    // --------------------

    // ResizeObserver - watches for content size changes within the component
    const ro = new ResizeObserver(() => {
      if (isModalOpenRef.current) return;
      ScrollTrigger.refresh();
    });
    ro.observe(row);
    ro.observe(vp);

    // Window events (debounced)
    const debouncedResize = debounce(onResize, RESIZE_DEBOUNCE_MS);
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', debouncedResize);

    // --------------------
    // Detect first "scroll action" to begin

    // Only create first scroll observer if showing intro and kick hasn't started yet
    log('Checking if should create first scroll observer');

    // Create the first scroll observer (desktop only)
    if (showIntro && !isKickStartedRef.current && !isReverseStartedRef.current) {
      log('Calling firstScrollObserverCreate');
      firstScrollObserverCreate();
      log('Finished calling firstScrollObserverCreate');
    } else {
      log('Skipping first scroll observer creation', { showIntro });
    }

    // --------------------
    // Initial setup

    // Delay initial setup to allow React to finish rendering with proper dimensions
    let setupTimer: number | undefined;
    setupTimer = window.setTimeout(() => {
      onResize();
      setIsAnimationsReady(true); // Mark animations as ready after resize completes
      logGreen('GSAP animations initialized and ready');
    }, 200);

    // --------------------
    // Cleanup

    return () => {
      // log('GSAP cleanup - unmounting');

      // Clear initialization timer
      if (setupTimer !== undefined) {
        clearTimeout(setupTimer);
      }

      // Remove window events
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', debouncedResize);

      // Destroy observers
      ro && ro.disconnect();
      firstScrollObserverDestroy();

      // Unlock body scroll and remove Lenis classes
      bodyScrollUnlock();
      lenisCleanupClasses();

      // Use cleanupAnimations for consistent cleanup
      cleanupAnimations();
    };

  }, [isClientReady]); // Re-run when client becomes ready (screen size is known)

  // -------------------- Render

  log('RockHallTv RENDER', { isKickComplete, showModal, channelItemTargetSize });

  return (
    <section
      ref={rootEl}
      id="main"
      className={cx(["rock-hall-tv", { debug: isDebugMode }, { portrait: isPortraitMode }, { "touch-device": mounted && isTouchDeviceState }])}
      {...(showModal ? { inert: '' as any } : {})}
    >
      <div className={cx("rock-hall-tv-content")}>

        {/* Header */}
        <div className={cx("header")} ref={headerRef}>

          {/* Left side: Text */}
          <div className={cx("header-text")} ref={headerTextRef}>
            <h1
              className={cx(["headline", { "touch-device": mounted && isTouchDeviceState }])}
              dangerouslySetInnerHTML={{ __html: headlineMarkup }}
            />
            <p className={cx(["subheadline"])} dangerouslySetInnerHTML={{ __html: subhead }} />
          </div>

          {/* Right side: Logo */}
          <div className={cx("logo")} ref={logoRef}>
            <RHTV_Logo />
          </div>

        </div>

        {/* CTA - Scroll to begin (desktop only) */}
        {showIntro && (
          <div className={cx("scroll-cta")} ref={scrollCtaRef}>
            <p className={cx("eyebrow-l")}>{scrollPrompt}</p>
            <div className={cx("cta-icon")}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 0C16.4183 0 20 3.58172 20 8V16C20 20.4183 16.4183 24 12 24C7.71994 24 4.22523 20.6389 4.01074 16.4121L4 16V8C4 3.58172 7.58172 0 12 0ZM12 2.5C8.96243 2.5 6.5 4.96243 6.5 8V16C6.5 19.0376 8.96243 21.5 12 21.5C15.0376 21.5 17.5 19.0376 17.5 16V8C17.5 4.96243 15.0376 2.5 12 2.5Z"
                  fill="currentColor"
                />
                <circle cx="12" cy="8" r="2" fill="currentColor" ref={scrollCtaCircleRef} />
              </svg>
            </div>
          </div>
        )}

        {/* Dark overlay - covers page elements during scale up */}
        <div className={cx("dark-overlay")} ref={overlayRef} />

        {/* Channels */}
        <div className={cx("channels-container")} ref={channelsContainerRef}>
          <RHTV_Channels
            channels={channels}
            rootRef={channelsViewportRef}
            rowRef={channelsRowRef}
            sectionLabel={rrtvAriaLabels.sectionLabel}
            channelLabel={rrtvAriaLabels.channelTitle}
            onChannelClick={handleChannelClick}
            onChannelFocus={handleChannelFocus}
            onTriggerKick={() => {
              // Trigger kick animation directly
              // log('onTriggerKick callback invoked from channel item');
              if (triggerKickCallbackRef.current) {
                triggerKickCallbackRef.current();
              }
            }}
            isKickComplete={isKickComplete}
            showModal={showModal}
            className={`${cx({ "not-interactive": !isKickComplete || showModal || isScaling || isReverseStarted })} ${!isKickStarted ? 'pre-kick' : ''}`}
            isPortraitMode={isPortraitMode}
            maxItemSize={channelItemTargetSize}
            isTouchDevice={isTouchDeviceState}
          />
        </div>

        {/* Modal */}
        <ClientPortal selector="portal" show={showModal}>
          <AnimatePresence mode="wait">
            {showModal && (
              <RHTV_Modal
                key="rhtv-modal"
                rrtvAriaLabels={rrtvAriaLabels}
                trayButtonLabels={{ videoTrayOpen, videoTrayClose }}
                // borderRadius={BORDER_RADIUS.MODAL}
                initial={{ opacity: 0 }}
                animate={{
                  // On touch devices: Simple fade in/out (no scale coordination needed)
                  // On non-touch devices: Fade in only after scale-up completes
                  opacity: isModalExiting ? 0 : (isTouchDeviceState || isScaleUpComplete ? 1 : 0)
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: isModalExiting ? 0.40 : (isTouchDeviceState ? 0.30 : 0.42),
                  ease: isModalExiting ? 'easeOut' : easeOutQuad
                }}
                onShouldExit={(shouldExit) => {
                  if (shouldExit) {
                    log('Modal exit triggered');
                    setIsModalExiting(true);

                    // Non-touch devices: Show channel row before modal starts fading out
                    if (!isTouchDeviceState && channelsRowRef.current) {
                      log('Non-touch: Showing channel row for scale down');
                      channelsRowRef.current.style.visibility = 'visible';
                    }
                  }
                }}
                onAnimationComplete={(definition: any) => {
                  // Hide channel row when modal fade-in completes (non-touch devices only)
                  if (definition.opacity === 1 && !isTouchDeviceState && channelsRowRef.current) {
                    log('Modal fade-in complete - hiding channel row');
                    channelsRowRef.current.style.visibility = 'hidden';
                  }
                }}
                onContentSizeChange={handleModalContentSizeChange}
                onCloseComplete={handleModalCloseComplete}
                isPortraitMode={isPortraitMode}
                isTouchDevice={isTouchDeviceState}
              />
            )}
          </AnimatePresence>
        </ClientPortal>

      </div>

      {/* Loading/Reload overlay - shown on page load and when switching between intro/no-intro modes (when showIntro state changes) */}
      <AnimatePresence>
        {(showLoadingOverlay || isReloading) && (
          <motion.div className={cx("loading-overlay")}
            initial={{ opacity: isReloading ? 0 : 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          >
            {/* <span className={cx("eyebrow-l")}>Loading</span> */}
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );

}
